/**
 * ─── THE INTAKE'S CONTROL-LEVEL COPY ─────────────────────────────────────────────
 * Every word that belongs to a CONTROL rather than to a page: the place-mode choice, the
 * shift and recurrence segments, the command modes, the force grid's hints and the basics
 * fields. It sits beside the components that render it (`Sections.tsx`, `ForceGrid.tsx`)
 * because both intake forms use those components and must not disagree about what a
 * control is called.
 *
 * ⚠️ WHAT DOES *NOT* LIVE HERE: a PAGE's own words — its steps, its publish bar, its help
 * panel, its section headings. Those differ between the two forms by design, and each page
 * keeps its own `copy.ts`.
 *
 * Written for a clerk who has never seen the system. The words "deployment", "slot",
 * "allocation", "detached", "stamp" and "policy version" appear nowhere.
 */
/**
 * ⚠️ [amended D4] THE PLACE-MODE COPY IS GONE BECAUSE ITS CONTROL IS.
 *
 * `concurrent`, `sequential` and `locationModePrompt` described a segment that decided
 * whether force was needed at every place at once. That question is now answered
 * STRUCTURALLY — two places at once is two duty points, one team touring two places is one
 * card with two stops — so there is nothing left to ask and nothing left to explain.
 */
export const PLACES_COPY = {
  freeText: 'Not every duty point is on the list. Type the name as you would write it on the order.',
  addPlace: 'Add another stop',
  /**
   * ⚠️ [D4.5 §2b] A SECOND CONSTANT, NOT A TERNARY ON `fields.length`.
   * "Add another stop" is right beside an existing row and wrong when there are none — which
   * is the state an operator reaches by saving a card before typing its place. The empty
   * state is the only place this is used, so the two words never have to be chosen at render
   * time and a reader has one line to look at rather than a branch.
   */
  addFirstPlace: 'Add the place',
  noPlacesYet: 'Add the place this duty point covers.',
} as const;

/**
 * ⚠️ [amended D4 §6] THE THREE SEGMENT SENTENCES ARE TOOLTIPS, NOT PARAGRAPHS, AND THEY
 * SAY "DUTY POINT" NOW. A schedule belongs to one card, so "every date this arrangement
 * covers" was about to become false on any order with two.
 */
export const TIMING_COPY = {
  /* ⚠️ [C2b §1b] It lives on the DATE row now — see `TimingSection`. */
  reportingTime: 'Reporting time',
  /* [STAND-DOWN] The optional end of a whole-day duty, revealed by `addStandDownTime`. */
  standDownTime: 'End time',
  addStandDownTime: 'Add stand down time',
  /* ⚠️ [C2b] Why the type control is read-only on a stored range — see `rangeTypeOption`. */
  storedRange: 'This order runs over a date range. The dates below are editable; the type is not.',

  /** The badge itself is two characters; the explanation is what stops it being a puzzle. */
  overnightBadge: 'This shift ends after midnight.',
  overnightExplained:
    'The end time is earlier than the start, so this shift runs past midnight and finishes the next morning. ' +
    'It still counts as one duty, on the date it started.',

  weekdays: 'The arrangement repeats on these days only.',
  addShift: 'Add shift',
  slotStrip: 'One chip per duty.',
} as const;

export const COMMAND_COPY = {
  /**
   * Three tooltips rather than one. They are three different answers to one question, and a
   * combined tooltip would be three sentences on a control that is already a choice.
   */
  modeAll: 'Everyone named above is on this shift.',
  modeAny: 'This shift is covered as long as at least one of them is there.',
  modeSpecific: 'Only the officers you tick are on this shift.',

  /** ⚠️ [D4 §6] A tooltip on the mapper rather than a paragraph above it. */
  perShiftIntro: 'More than one officer and more than one shift — set who covers which.',
  officerPicker: 'Search by name or phone, and check the rank and posting before you pick.',
  requiredRole: 'This policy expects this role to be filled before the arrangement is published.',

  /* ── [D4.3] FR-5.2.6c for command ─────────────────────────────────────────────── */

  addAdHoc: 'Add role',
  addAdHocLabel: 'Add a one-off command role',
  addAdHocTitle: 'Add a one-off command role',
  /**
   * ⚠️ THE TOOLTIP IS WHERE THE COST IS STATED, AND IT IS THE ONLY PLACE IT CAN BE.
   *
   * `Add role` does not say "one-off", exactly as `Add column` does not — so this sentence
   * carries both halves: it changes nothing outside this card, AND it is invisible to any
   * report that compares command across policies, because it has no policy key behind it.
   * A role that has to be comparable is a POLICY role, and minting one is an admin action.
   */
  adHocRole:
    'A command role for this duty point only. It does not change the policy, and because it has no policy role behind it, it will not appear in reporting that compares command across policies.',
  adHocTag: 'One-off',
  removeAdHocLabel: (label: string) => `Remove the one-off command role ${label}`,
  removeAdHocTitle: (label: string) => `Remove ${label}?`,
  /**
   * ⚠️ IT STATES WHAT IS LOST, NOT WHETHER THE OPERATOR IS SURE. A one-off role has no
   * policy row to fall back to, so removing it discards the names with it.
   */
  removeAdHocBody: (officerIds: readonly string[]) =>
    officerIds[1] === undefined
      ? 'The officer named for this role will be removed with it. This role is not part of the policy, so nothing else changes.'
      : `The ${String(officerIds.length)} officers named for this role will be removed with it. This role is not part of the policy, so nothing else changes.`,
  /** ⚠️ [C6a review] The same removal of a role ASKED of a division — what goes is the ask, not a name. */
  removeAdHocAskedBody:
    'This role is asked of a division, and the ask is removed with it. This role is not part of the policy, so nothing else changes.',

  /* ── [C6a §2] asking a division for a command role ─────────────────────────────── */

  /**
   * ⚠️ TWO WAYS TO FILL ONE ROLE, SAID AS A CHOICE. "By name" stays the default — every order
   * raised before C6a is one — and "Ask a division" hands the naming to the division.
   */
  byName: 'By name',
  askDivision: 'Ask a division',
  modeGroup: (role: string): string => `How ${role} is filled`,
  askedOf: 'Asked of',
  /**
   * ⚠️ A FIGURE ONLY WHERE ONE ADDRESSEE IS ASKED FOR MORE THAN ONE POSITION, AND ON ITS OWN LINE.
   * It is how many people the division is asked to NAME for this role — never a force figure,
   * and never beside one (FR-5.2.6b).
   */
  askPositions: (positions: number, code: string): string => `${String(positions)} positions asked of ${code}`,
  askChange: 'Change',
  askRemove: 'Remove',
  askChangeLabel: (role: string): string => `Change who ${role} is asked of`,
  askRemoveLabel: (role: string): string => `Remove the division ask for ${role}`,
  askAddRow: 'Ask another division',
  askConfirm: 'Confirm',
  askCancel: 'Cancel',
  askRefused: 'That ask could not be saved.',
  askCount: (role: string, row: number): string => `How many ${role} to ask for, row ${String(row)}`,
  askTargetContext: (role: string, row: number): string => `${role}, row ${String(row)}`,
  /**
   * ⚠️ WHAT HAPPENS NEXT, BEFORE THE CLICK. The division — not this screen — names the officers,
   * or hands a position on to one of its units, once the order is published.
   */
  askHint:
    'Once the order is published, the division names the officers from its fill screen, or hands a position to one of its units. Leave the unit as "Any unit" to let the division choose.',
  /** ⚠️ IT STATES WHAT IS LOST. A role is either named or asked of a division, never both. */
  askReplacesNamed: (role: string): string => `Confirming removes the officers named for ${role}.`,
  /**
   * ⚠️ [C6a review] "BY NAME" ON AN ASKED ROLE DELETES THE ASK, SO IT SAYS SO BEFORE IT DOES. The switch
   * reads as a choice, and pressed on a confirmed ask it wrote `asks: []` and autosaved with no word —
   * the one ask action that stated nothing of what it lost. Remove's own label already names the loss.
   */
  askDropTitle: (role: string): string => `Name ${role} by name instead?`,
  askDropBody: (role: string): string =>
    `This removes the division ask for ${role} from this card. You can then name the officer here.`,
  askDropConfirm: 'Remove the ask',
  /** An issued order: a position nobody has been named to yet, and who owes it. */
  awaitedFrom: (code: string): string => `Awaited from ${code}`,
  /**
   * ⚠️ [C6a review] THE SAME POSITION ON AN ORDER THAT IS OVER OR WITHDRAWN — nobody can name it any
   * more, so it is not "awaited". The record page and the preview say it in these words
   * (`PREVIEW_COPY.notNamedBy`); the builder, a third reader of the same seat, says it the same way.
   */
  notNamedBy: (code: string): string => `Not named by ${code}`,
  /**
   * ⚠️ A ROLE ASKED OF A DIVISION THAT THIS CARD'S POLICY VERSION NO LONGER CARRIES. The publish
   * preview blocks on it ("Choose the role again, or remove it"), so the builder must show the row
   * and the one act that clears it — without it the blocker names something nobody can see.
   */
  notOnPolicy: "No longer on this card's policy. Remove this ask, then ask for a role the policy has.",
} as const;

export const FORCE_COPY = {
  /** ⚠️ [D4 §6] A TOOLTIP on the heading now, rather than a paragraph above the grid. */
  gridHint: 'Type how many you need, then say which divisions are sending them.',

  /**
   * ⚠️ THE ONE INSTRUCTION THIS SCREEN GIVES. Everything else about the grid is meant to be
   * learnable from the numbers moving.
   */
  remaining: 'How many are still to be found. Keep adding until every number here is zero.',

  anyUnit: 'Leave this blank and the division chooses which of its units sends the officers.',
  splitEqually: 'Shares each total evenly between the divisions you have added.',
  noSourcesYet: 'Add a division to say where the force is coming from.',
  /**
   * ⚠️ THE ONE SENTENCE THAT MUST SURVIVE THE RENAME. `One-off column` said "one-off" in
   * the label; `Add column` does not, so the tooltip is now the only place an operator is
   * told this does not change the policy — which is FR-5.2.6c's whole point.
   */
  adHocColumn: 'A column for this duty point only. It does not change the policy.',
  sourceNeedsDivision: 'Choose a division on the empty row before adding another.',

  /* ── [D4.3] taking a one-off column back ──────────────────────────────────────── */

  restrictAdHocTitle: 'Which posts may fill this column?',
  restrictAdHoc:
    'A one-off column accepts any officer by default. Naming posts narrows the search to the ranks those posts accept — for this order only.',
  restrictAdHocAny: 'Any post',
  restrictAdHocLabel: (label: string) => `Restrict which posts may fill ${label}`,
  removeAdHocLabel: (label: string) => `Remove the one-off column ${label}`,
  removeAdHocTitle: (label: string) => `Remove ${label}?`,
  /**
   * ⚠️ IT STATES WHAT IS LOST. A one-off column is not in the policy, so removing it takes
   * its numbers and its sources with it and nothing puts them back. On an order that has
   * already been published the slots go with it too — and a slot that disappears while it
   * holds a name is CANCELLED and surfaced under "no longer required", never deleted, which
   * is why this says the names are moved rather than destroyed.
   */
  removeAdHocBody: (asked: number) =>
    asked > 0
      ? `This column asks for ${String(asked)} and is not part of the policy, so its numbers and sources go with it. Anyone already named against it on a published order moves to "no longer required".`
      : 'This column is not part of the policy, so its sources go with it.',

  /* ── [C5c §2] one person, many duty points ──────────────────────────────────────── */

  /** Under an anchor's column header: the number typed here is the whole order's. */
  sharedHere: 'Covers every duty point',
  /** The header group over the columns another card answers for. */
  sharedGroup: 'Shared',
  sharedFrom: (anchor: string) => `from ${anchor}`,
  /** A covered cell's accessible name — the dash says nothing on its own. */
  coveredCell: (column: string, anchor: string, count: number) =>
    `${column}: shared from ${anchor}, which asks for ${String(count)}`,
  /**
   * [C5c follow-up] The share toggle's VISIBLE words, beside its link icon — an icon alone read as
   * decoration. The accessible names below START with the same words (WCAG 2.5.3, label in name), so
   * a voice-control user can say what they see.
   */
  linkAll: 'Link All',
  shareColumn: (column: string) => `Link All — ask for ${column} once, across every duty point`,
  stopSharing: (column: string) => `Link All — stop sharing ${column}`,
} as const;

export const SHIFT_COPY = {
  sameForAll: 'Every shift asks for the same force. You can still change one shift on its own.',
  /**
   * "Different here" is the word an operator reads on the button; the tooltip says what it
   * COSTS them, which is the part they cannot see — that this shift stops following the
   * others for good.
   */
  differentHere: 'Sets this shift apart. Later changes to the others will leave this one alone.',
  detachedBadge: 'This shift was changed on its own.',
} as const;

export const BASICS_COPY = {
  /**
   * ⚠️ [C3c §9a] IT NAMES WHAT IS LOCKED AND WHAT IS NOT. Only the identity is frozen; the
   * description, the associated units and the attachments are all still open, and a sentence
   * that said "this order cannot be edited" would be false of most of the screen.
   */
  identityLocked:
    'This order has started, so its order date and crowd estimate are fixed. You can still edit the PA order number, the title, the description, the associated stations and the files.',
  policyChips: 'The policy decides which kinds of post this arrangement can ask for.',
  paNumber: 'Your own reference for this order, if your office uses one.',
  /**
   * ⚠️ [C1b §4b] "VISIBILITY ONLY" IS THE WHOLE SENTENCE, AND IT IS NOT A CAVEAT.
   *
   * An operator reading "associated units" beside a force grid reads it as an obligation.
   * Confirmed with the client that it is not one, so the words say so twice — once in the
   * tooltip and once under the field, where somebody who never hovers still sees it.
   */
  associatedUnits:
    'Units that should be able to SEE this order. They may be in any division. This asks them for nobody.',
  associatedUnitsHint: 'They appear in To Assign only if a force row names them.',
  /** ⚠️ The placeholder is the derived title, so this explains why it is grey. */
  titleDerived: 'Leave this blank and the order is titled from its place and date.',
  paDate: 'The date printed on the order. The duty dates come from the schedule below.',
} as const;

export const ENDS_COPY = {
  label: 'Ends',
  onDate: 'On a date',
  afterN: 'After',
  onDateHint: 'The arrangement runs up to and including this date.',
  afterNHint:
    'Repeat a fixed number of times and let the system work out the last date. Changing the days of the week keeps the same number of duties and moves the last one.',
  occurrencesLabel: 'times',
  lastDuty: 'Last duty',
} as const;
